import { jsPDF } from 'jspdf';
import { EvictionsByYearPerMonth, RiskByYearMonthDistrict, ReportFilters, InfrastructureByYearPerMonth, PreventionsByYearPerMonth } from '../../types/types';
import { drawEvictionNumbersSection } from '../../utils/summary_report/sections/evictionNumbersSection';
import { drawInfrastructureSection } from '../../utils/summary_report/sections/infrastructureSection';
import { drawPreventionSection } from '../../utils/summary_report/sections/preventionSection';
import { drawAreasAtRiskSection } from '../../utils/summary_report/sections/areasAtRiskSection';
import { drawReportHeader } from '../../utils/summary_report/reportHeader';
import { drawReportFooter } from '../../utils/summary_report/reportFooter';
import { registerJetBrainsMono, loadJetBrainsMonoBase64 } from '../../utils/summary_report/registerFonts';
import { loadLogosBase64 } from '../../utils/summary_report/loadLogos';
import { loadReportIconsBase64, ReportIconSet } from '../../utils/summary_report/loadReportIcons';
import { GeoFeatureCollection } from '../../utils/summary_report/geojsonUtils';
import { layout } from '../../theme/themes';
import { setRepeatedHeaderHeight } from '../../utils/summary_report/pdfPrimitives';

const now = new Date();
const timestamp = new Intl.DateTimeFormat("en-KE", {
  timeZone: "Africa/Nairobi",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false
})
  .format(now)
  .replace(/, /g, "_")
  .replace(/[/:]/g, "");

export interface BuildReportOptions {
  /** base64 image data URI. Omit to auto-load from /assets/logos/logo-left.png (see loadLogos.ts). */
  logoLeft?: string;
  /** base64 image data URI. Omit to auto-load from /assets/logos/logo-right.png. */
  logoRight?: string;
  /** District boundaries for section 04's map. Omitted → dashed placeholder instead. */
  geojson?: GeoFeatureCollection;
  /**
   * Pre-fetched font payloads — mainly for non-browser contexts (tests,
   * server-side rendering) where `fetch('/fonts/...')` isn't available.
   * In the browser this defaults to loadJetBrainsMonoBase64(), which
   * expects JetBrainsMono-Regular.ttf/Bold.ttf under your app's
   * public/fonts/ directory.
   */
  fontBase64?: { regular: string; bold: string };
  /**
   * Pre-fetched card icon images — mainly for non-browser contexts. In the
   * browser this defaults to loadReportIconsBase64(), which expects the 7
   * icon PNGs under your app's public/assets/icons/ directory. Omitting
   * (and a failed auto-load) falls back to the built-in vector icons.
   */
  iconsBase64?: ReportIconSet;
  /** Header: "Prepared by NRC Somalia — HLP AoR" by default. */
  preparedBy?: string;
  /** Header: rolling reference ID, e.g. "NRC-SOM-EVI-0001". Auto-computed from the date if omitted. */
  reportId?: string;
  /** Footer: "© 2026 Norwegian Refugee Council" by default. */
  organization?: string;
  /** Footer: "Source: ProdEvictionsReview" by default. */
  dataSource?: string;
  /** Footer confidentiality line — has a sensible default given this report's data sensitivity. */
  confidentialityNotice?: string;
}

/**
 * Builds the report PDF from data you already have in state. Doesn't
 * fetch anything itself — keeps data-fetching and PDF-building as
 * separate concerns.
 *
 * Section order: 01 eviction numbers, 02 infrastructure destroyed,
 * 03 prevention/response efforts, 04 areas at risk. `riskData`/
 * `infrastructureData`/`preventionData` are each optional so the report
 * still works while their endpoints are being wired up — omitting any
 * one just skips that section.
 *
 * The header is drawn once on page 1 to measure its real height (it can
 * vary — e.g. with/without a month filter), then that height is handed to
 * `ensureSpace` via `setRepeatedHeaderHeight` so every automatic page
 * break already leaves room for it at the top. The header is then redrawn
 * on every page in a final pass, exactly like the footer — "Page X of Y"
 * and the repeated header both need the true final page count, which
 * isn't known until every section (including any ensureSpace page
 * breaks) has actually been drawn.
 */
export async function buildEvictionReport(
  evictionsData: EvictionsByYearPerMonth,
  filters: ReportFilters,
  riskData?: RiskByYearMonthDistrict,
  infrastructureData?: InfrastructureByYearPerMonth,
  preventionData?: PreventionsByYearPerMonth,
  options?: BuildReportOptions,
): Promise<jsPDF> {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const contentWidth = doc.internal.pageSize.getWidth() - layout.pageMarginX * 2;
  const now = new Date(); // single generation timestamp shared by header + footer

  const fontBase64 = options?.fontBase64 ?? (await loadJetBrainsMonoBase64());
  registerJetBrainsMono(doc, fontBase64.regular, fontBase64.bold);

  // Auto-load the default logo assets unless the caller passed their own —
  // falls back to the dashed placeholder box (inside drawReportHeader) if
  // even the placeholder files are missing, so a broken path never crashes
  // report generation.
  let logoLeft = options?.logoLeft;
  let logoRight = options?.logoRight;
  if (!logoLeft || !logoRight) {
    try {
      const logos = await loadLogosBase64();
      logoLeft = logoLeft ?? logos.left;
      logoRight = logoRight ?? logos.right;
    } catch {
      // no logo assets available yet — header falls back to dashed boxes
    }
  }

  // Auto-load the 7 card icon images the same way — falls back to the
  // built-in vector icons (house/person/pin/warning outlines) per-card if
  // the real artwork isn't available, so a broken path never blanks a card.
  let icons: ReportIconSet | undefined = options?.iconsBase64;
  if (!icons) {
    try {
      icons = await loadReportIconsBase64();
    } catch {
      // no icon assets available yet — cards fall back to vector icons
    }
  }

  const headerTopY = 40;
  const headerArgs = {
    x: layout.pageMarginX,
    width: contentWidth,
    filters,
    logoLeft,
    logoRight,
    now,
    preparedBy: options?.preparedBy,
    reportId: options?.reportId,
  };

  let y = drawReportHeader(doc, { ...headerArgs, y: headerTopY });

  // Header height can vary (e.g. a month filter adds a line) — measure it
  // from page 1's actual draw rather than hardcoding it, and tell
  // ensureSpace so every mid-report page break leaves the same space free
  // at the top for the repeated header drawn in the loop below.
  setRepeatedHeaderHeight(y - headerTopY);

  y = drawEvictionNumbersSection(doc, evictionsData, filters, y, contentWidth, {
    households: icons?.evictionHouseholds,
    individuals: icons?.evictionIndividuals,
  });

  if (infrastructureData) {
    y = drawInfrastructureSection(doc, infrastructureData, filters, y, contentWidth);
  }

  if (preventionData) {
    y = drawPreventionSection(doc, preventionData, filters, y, contentWidth, {
      households: icons?.preventionHouseholds,
      individuals: icons?.preventionIndividuals,
    });
  }

  if (riskData) {
    y = drawAreasAtRiskSection(doc, riskData, filters, y, contentWidth, options?.geojson, {
      districts: icons?.riskDistricts,
      totalHouseholds: icons?.riskTotalHouseholds,
      extreme: icons?.riskExtreme,
    });
  }

  const totalPages = doc.getNumberOfPages();
  for (let pageNumber = 1; pageNumber <= totalPages; pageNumber++) {
    doc.setPage(pageNumber);

    // Page 1 already has its header from the measuring draw above — redrawing
    // it here would just paint over the same pixels, so only pages 2+ need it.
    if (pageNumber > 1) {
      drawReportHeader(doc, { ...headerArgs, y: headerTopY });
    }

    drawReportFooter(doc, {
      x: layout.pageMarginX,
      width: contentWidth,
      pageNumber,
      totalPages,
      generatedAt: now,
      organization: options?.organization,
      dataSource: options?.dataSource,
      confidentialityNotice: options?.confidentialityNotice,
    });
  }

  return doc;
}

/** Convenience wrapper: build and immediately trigger a browser download. */
export async function downloadEvictionReport(
  data: EvictionsByYearPerMonth,
  filters: ReportFilters,
  riskData?: RiskByYearMonthDistrict,
  infrastructureData?: InfrastructureByYearPerMonth,
  preventionData?: PreventionsByYearPerMonth,
  options?: BuildReportOptions & { filename?: string },
): Promise<void> {
  const doc = await buildEvictionReport(data, filters, riskData, infrastructureData, preventionData, options);
  doc.save(options?.filename ?? `NRC_SOM_EVI_${timestamp}_report.pdf`);
}